import { isRatedLine, sumRatedRate, resolveRateUnit } from '../reports/taxRate';

// ═══ TOTAL THE SCANNED MILLAGE LINES, PER JURISDICTION, AS A SUGGESTION ═══
//
// Entering a per-jurisdiction rate means adding up the notice's millage lines by hand. On Fruitland Park
// (Lake County, 2026) that is nine lines summing to 10.5416 for the County and two summing to 6.0540 for the
// schools — done on four parcels, one got it wrong in someone's head. The lines are already stored, with each
// line's own taxable_value, so the arithmetic can be offered.
//
// ⛔ A SUGGESTION, NEVER A FILL. A wrong rate is the 10x class of error, and the TS reading the figure back
// against the notice IS the check. Nothing here writes; it returns what to offer.
//
// ⛔ AND IT REFUSES FAR MORE READILY THAN IT OFFERS. Every case it cannot be sure about returns a `note`
// instead of a figure — because silence is indistinguishable from there being no scanned bill, which is the
// state someone is in when they are adding lines up by hand wondering whether the feature exists.
// ⛔ THE SUM IS ROUNDED TO THE PRECISION ITS INPUTS WERE PRINTED AT, AT THE POINT IT IS COMPUTED.
// Eight Lake County millages printed to four decimals summed to 10.954999999999998 in binary floating point,
// and the suggestion did not merely DISPLAY that — clicking it stored it, so the database carried a rate no
// document states. (Schools' two lines happened to land exactly, at 6.054, which is why it looked fine there.)
//
// ⛔ NOT A FIXED FOUR DECIMALS, which is what the symptom suggests. A Florida TRIM prints millage to 4, but a
// California bill prints percent to SIX — 1.000000 and 0.043759 sum to 1.043759, and rounding that to 4 would
// store 1.0438, losing real precision and inventing a different wrong number. So the precision comes from the
// contributing lines themselves: the most decimals any of them was printed with.
//
// Rounded HERE rather than at display, because this is the figure that gets written. sumRatedRate is left
// alone: it is shared with the scanner, the breakdown and resolveRateUnit, none of which store its result, and
// a rounding rule belongs where the value lands rather than in everything that ever adds rates up.
const DP_CAP = 8;   // no rate document prints beyond this; caps the exponent if a stored rate already carries noise
const decimalsOf = (raw) => {
  const s = String(raw ?? '').replace(/[^0-9.]/g, '');
  const i = s.indexOf('.');
  return i < 0 ? 0 : Math.min(DP_CAP, s.length - i - 1);
};
const roundToInputPrecision = (sum, lines) => {
  if (sum == null) return null;
  const dp = Math.max(0, ...lines.map((l) => decimalsOf(l.tax_rate)));
  const f = 10 ** dp;
  return Math.round(sum * f) / f;
};

export function suggestJurisdictionRates(lines, rows, opts = {}) {
  const { year = '', source = 'bill' } = opts;
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  const jurRows = (Array.isArray(rows) ? rows : []).filter((r) => r && r.jurisdiction);
  const collectors = new Set(list.map((l) => l._collectorId).filter(Boolean));
  const fromNotice = source === 'notice';
  const none = (note) => ({ byJurisdiction: {}, note, lineCount: 0, collectorCount: collectors.size, source });

  // ⛔ A LINE THE SYSTEM ALREADY DOUBTS CANNOT BE TOTALLED AROUND. Notice lines carry _reconciled:false when
  // taxable x rate did not equal the notice's own tax figure — kept rather than dropped, because a silently
  // dropped line is the failure that capture exists to make visible (7452220A0030). Summing past the flag
  // would reintroduce exactly the quiet wrong the flag records. Bill lines have no equivalent, so this check
  // only ever fires on the notice path.
  const flagged = list.filter((l) => l._reconciled === false).length;
  if (flagged) {
    return none(`${flagged} of the ${list.length} rate lines from the ${year} notice do not reconcile against the notice's own tax figures, so they are not totalled. Check those lines, or enter the rate from the notice.`);
  }

  // ⛔ NOTICE LINES WITH NO TAX AMOUNT CANNOT RESOLVE A UNIT, and that reads differently from having no notice
  // at all — which is the distinction someone needs when the suggestion is quiet. resolveRateUnit would report
  // its generic "no line carries a rate, a taxable value and an amount together"; this says which half is
  // missing and what to do.
  if (fromNotice && list.length && !list.some((l) => l.amount != null && l.amount !== '')) {
    return none(`The ${year} notice's rate lines carry no tax amounts, so millage cannot be told from percent. Enter the rate and its unit from the notice.`);
  }

  const baseOf = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n !== 0 ? Math.round(n * 100) / 100 : null;
  };
  // ⛔ RATED LINES WITH A BASE ONLY. A flat assessment contributes no rate and carries no taxable value, so
  // including it would invent a group. isRatedLine is classifyLine — the one classifier — so a rate lost to a
  // wrapped line is recovered rather than mis-filed here and nowhere else.
  const rated = list.filter((l) => isRatedLine(l) && baseOf(l.taxable_value) != null);
  if (!rated.length) {
    return none(list.length
      ? `No rate-bearing lines on the ${fromNotice ? `${year} notice` : 'scanned bill'} — nothing to total.`
      // ⛔ NAMES BOTH SOURCES, because by the time this fires the bill path has already been tried and fallen
      // through to the notice. "No scanned bill" alone would send someone looking for a bill in August.
      : `No bill lines and no notice rate table for ${year} — nothing to total. Enter the rate from the notice.`);
  }

  // ⛔ THE UNIT COMES FROM THE LINES, VIA resolveRateUnit — which reconciles each line against ITS OWN base,
  // so it survives a two-base bill where detectRateUnit does not. Not confident means NO suggestion: offering
  // a number without knowing whether it is mills or percent is the factor-of-ten error with a friendly face.
  const unit = resolveRateUnit(rated);
  if (!unit.confident) {
    return none(`Could not tell millage from percent on this bill (${unit.reason}), so the lines are not totalled. Enter the rate from the notice.`);
  }

  const groups = new Map();
  rated.forEach((l) => {
    const k = String(baseOf(l.taxable_value));
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(l);
  });

  // ⛔ THE CASE THIS MOST OFTEN MEETS, AND IT MUST NOT GUESS. Florida's two bases differ only when the
  // non-homestead cap actually bit; a parcel that rose less than 10% has ONE base, so school and non-school
  // lines fall into one group whose sum (16.5956 on Fruitland Park) is the right rate for NEITHER row. Most
  // parcels in most years are under the cap, so this is the common shape, not the edge.
  // Splitting by name — Florida school lines read "SCHOOL…" — is a convention of one county's print layout,
  // not a guarantee, and is deliberately not done here (docs/known-debt.md).
  if (groups.size === 1 && jurRows.length > 1) {
    return none(`Both authorities tax the same base this year, so the ${rated.length} lines can't be split by value. Enter each rate from the notice.`);
  }
  if (groups.size !== jurRows.length) {
    return none(`The bill has ${groups.size} distinct taxable base${groups.size === 1 ? '' : 's'} and this year has ${jurRows.length} jurisdiction row${jurRows.length === 1 ? '' : 's'} — they don't correspond, so the lines are not totalled.`);
  }

  // One-to-one on the base, or nothing. A near-miss is a figure attached to the wrong authority.
  const byJurisdiction = {};
  const used = new Set();
  for (const r of jurRows) {
    const k = String(baseOf(r.taxable_total));
    if (k === 'null' || !groups.has(k) || used.has(k)) return none(
      `The bill's taxable bases don't match this year's taxable totals, so the lines can't be matched to a jurisdiction. Check the values against the notice.`);
    used.add(k);
    const g = groups.get(k);
    const rate = roundToInputPrecision(sumRatedRate(g), g);
    if (rate == null) return none(`One group of lines carries no readable rate, so the totals are not offered.`);
    byJurisdiction[r.jurisdiction] = { rate, unit: unit.unit, lineCount: g.length };
  }
  return { byJurisdiction, note: null, lineCount: rated.length, collectorCount: collectors.size, source, year };
}

// The sentence offered beside a row: what was added up, and from where.
// ⛔ THE COLLECTOR COUNT IS NAMED RATHER THAN HIDDEN. Lines are pooled across a parcel's collectors because
// blocking would withhold help on a case that is probably fine — but a two-bill parcel must SAY it is two, so
// a merge that misleads is caught by the TS reading it against the notice rather than passing silently.
// ⛔ THE SOURCE IS NAMED, because a figure off a TRIM and a figure off a bill are not the same claim. A bill's
// rate is what was levied; a notice's proposed rate is what the authority intends to levy and can still change.
// Someone accepting the suggestion should know which they are accepting.
export function suggestionLabel(s, jurisdiction) {
  const hit = s && s.byJurisdiction[jurisdiction];
  if (!hit) return null;
  const from = s.source === 'notice' ? ` from the ${s.year} notice`
    : s.collectorCount > 1 ? ` across ${s.collectorCount} collectors` : '';
  const n = hit.lineCount;
  return `Σ ${n} line${n === 1 ? '' : 's'}${from} = ${hit.rate} ${hit.unit === 'mills' ? 'mills' : '%'}`;
}
